import { useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import type {
  OpticlockEntry,
  OpticlockEntryPatchInput,
  OpticlockProject,
} from '@staff-app/shared-types';
import { DURATION_OPTIONS, formatHoursMinutes } from '../lib/duration';
import { ProjectDot } from '../lib/project-colors';

interface EntryTableProps {
  entries: OpticlockEntry[];
  projects: OpticlockProject[];
  loading: boolean;
  totalMinutes: number;
  onUpdate: (id: string, patch: OpticlockEntryPatchInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

/**
 * The month's entries, with inline edit.
 *
 * Loading and empty are ROWS INSIDE the table, not page states — the house convention, and it
 * keeps the header and the month total on screen while a refetch is in flight.
 *
 * A row edited by root on someone else's behalf carries `updatedBy`; it is surfaced, because a
 * row whose numbers someone else changed should say so rather than looking self-reported.
 */
export function EntryTable({
  entries,
  projects,
  loading,
  totalMinutes,
  onUpdate,
  onDelete,
}: EntryTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<OpticlockEntryPatchInput>({});
  const [busy, setBusy] = useState(false);

  function startEdit(e: OpticlockEntry): void {
    setEditingId(e._id);
    setDraft({
      projectId: e.projectId,
      entryDate: e.entryDate,
      minutes: e.minutes,
      note: e.note ?? '',
    });
  }

  async function save(id: string): Promise<void> {
    setBusy(true);
    try {
      await onUpdate(id, draft);
      setEditingId(null);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    try {
      await onDelete(id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Table data-testid="opticlock-entry-table">
        <TableHeader>
          <TableRow>
            <TableHead>{'Day'}</TableHead>
            <TableHead>{'Project'}</TableHead>
            <TableHead>{'Time'}</TableHead>
            <TableHead>{'Note'}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-xs text-muted-foreground" data-testid="opticlock-loading">
                {'Loading…'}
              </TableCell>
            </TableRow>
          ) : null}
          {!loading && entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-xs text-muted-foreground" data-testid="opticlock-empty">
                {'Nothing logged this month yet — add your first entry above.'}
              </TableCell>
            </TableRow>
          ) : null}

          {entries.map((e) => {
            const editing = editingId === e._id;
            return (
              <TableRow key={e._id} data-testid={`opticlock-row-${e._id}`}>
                <TableCell data-testid={`opticlock-row-${e._id}-date`}>
                  {editing ? (
                    <Input
                      type="date"
                      className="w-[9.5rem]"
                      value={draft.entryDate ?? ''}
                      aria-label="Day worked"
                      onChange={(ev) => setDraft({ ...draft, entryDate: ev.target.value })}
                    />
                  ) : (
                    e.entryDate
                  )}
                </TableCell>

                <TableCell data-testid={`opticlock-row-${e._id}-project`}>
                  {editing ? (
                    <Select
                      value={draft.projectId ?? ''}
                      onValueChange={(v) => setDraft({ ...draft, projectId: v })}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p._id} value={p._id}>
                            <span className="flex items-center gap-2">
                              <ProjectDot color={p.color} />
                              {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ProjectDot color={e.projectColor} />
                      <span>{e.projectName ?? e.projectId}</span>
                    </span>
                  )}
                </TableCell>

                <TableCell className="tabular-nums" data-testid={`opticlock-row-${e._id}-minutes`}>
                  {editing ? (
                    <Select
                      value={String(draft.minutes ?? e.minutes)}
                      onValueChange={(v) => setDraft({ ...draft, minutes: Number(v) })}
                    >
                      <SelectTrigger className="w-[110px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={String(o.value)}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    formatHoursMinutes(e.minutes)
                  )}
                </TableCell>

                <TableCell className="max-w-[22rem]" data-testid={`opticlock-row-${e._id}-note`}>
                  {editing ? (
                    <Input
                      value={draft.note ?? ''}
                      aria-label="Note"
                      onChange={(ev) => setDraft({ ...draft, note: ev.target.value })}
                    />
                  ) : (
                    <span className="flex flex-col">
                      <span className="truncate text-sm">{e.note ?? '—'}</span>
                      {e.updatedBy ? (
                        <span className="text-xs text-muted-foreground">
                          {`edited by ${e.updatedBy}`}
                        </span>
                      ) : null}
                    </span>
                  )}
                </TableCell>

                <TableCell>
                  <div className="flex justify-end gap-2">
                    {editing ? (
                      <>
                        <Button
                          size="sm"
                          disabled={busy}
                          data-testid={`opticlock-row-${e._id}-save`}
                          onClick={() => save(e._id)}
                        >
                          {'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          data-testid={`opticlock-row-${e._id}-cancel`}
                          onClick={() => setEditingId(null)}
                        >
                          {'Cancel'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`opticlock-row-${e._id}-edit`}
                          onClick={() => startEdit(e)}
                        >
                          {'Edit'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          data-testid={`opticlock-row-${e._id}-delete`}
                          onClick={() => remove(e._id)}
                        >
                          {'Delete'}
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* While the first fetch is in flight we do NOT know the total, and rendering a
          confident "0:00" claims the agent logged nothing — which is a different statement
          from "not loaded yet", and the one an agent would read as their month being empty.
          An e2e spec read this placeholder as real data; a person would too. */}
      <p className="text-sm font-semibold" data-testid="opticlock-month-total">
        {loading && entries.length === 0
          ? 'This month: —'
          : `This month: ${formatHoursMinutes(totalMinutes)}`}
      </p>
    </div>
  );
}
